import React, { useState, useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { fetchMonthlyReport } from '../services/api';
import './DailyReportModal.css';

function MonthlyReportModal({ isOpen, onClose }) {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const reportRef = useRef(null);

  // 모달이 열릴 때 월간보고서 데이터 로드
  useEffect(() => {
    if (isOpen) {
      loadReportData();
    }
  }, [isOpen]);

  // 월간보고서 데이터 로드 (데모용 - mock API)
  const loadReportData = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await fetchMonthlyReport();
      setReportData(data);

    } catch (err) {
      console.error('Failed to load monthly report:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // PDF 다운로드
  const handleDownloadPDF = async () => {
    if (!reportData || !reportRef.current) return;

    try {
      setIsPdfGenerating(true);

      // 보고서 영역의 HTML을 캡처
      const reportElement = reportRef.current;

      // 원래 스타일 저장
      const originalOverflow = reportElement.style.overflow;
      const originalMaxHeight = reportElement.style.maxHeight;
      const scrollPosition = reportElement.scrollTop;

      // PDF 생성을 위해 임시로 스크롤 제거 및 전체 높이 표시
      reportElement.style.overflow = 'visible';
      reportElement.style.maxHeight = 'none';
      reportElement.scrollTop = 0;

      // PDF 생성을 위해 임시로 닫기 버튼 숨기기
      const closeBtn = reportElement.querySelector('.close-btn');
      const pdfBtn = reportElement.querySelector('.pdf-btn');
      if (closeBtn) closeBtn.style.display = 'none';
      if (pdfBtn) pdfBtn.style.display = 'none';

      // 약간의 지연을 주어 DOM이 완전히 렌더링되도록 함
      await new Promise(resolve => setTimeout(resolve, 100));

      // HTML을 캔버스로 변환 (전체 높이 캡처)
      const canvas = await html2canvas(reportElement, {
        scale: 2, // 고해상도
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowHeight: reportElement.scrollHeight,
        height: reportElement.scrollHeight
      });

      // 원래 스타일 복원
      reportElement.style.overflow = originalOverflow;
      reportElement.style.maxHeight = originalMaxHeight;
      if (closeBtn) closeBtn.style.display = 'flex';
      if (pdfBtn) pdfBtn.style.display = 'flex';
      reportElement.scrollTop = scrollPosition;

      // 캔버스를 이미지로 변환
      const imgData = canvas.toDataURL('image/png');

      // PDF 생성 (A4 사이즈)
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      // 픽셀을 mm로 변환 (96 DPI 기준)
      const imgWidthMM = (imgWidth * 25.4) / 96 / 2; // scale 2 적용
      const imgHeightMM = (imgHeight * 25.4) / 96 / 2; // scale 2 적용

      // PDF 너비에 맞게 비율 조정
      const ratio = pdfWidth / imgWidthMM;
      const scaledImgHeight = imgHeightMM * ratio;

      let heightLeft = scaledImgHeight;
      let position = 0;
      let pageCount = 0;

      // 첫 페이지
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, scaledImgHeight);
      heightLeft -= pdfHeight;
      pageCount++;

      // 나머지 페이지들 - 각 페이지마다 다음 부분을 표시
      while (heightLeft > 0) {
        position = -(pdfHeight * pageCount);
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, scaledImgHeight);
        heightLeft -= pdfHeight;
        pageCount++;
      }

      // PDF 파일명 생성
      const fileName = `월간보고서_${reportData.header.category}_${reportData.header.year}년${reportData.header.month}월.pdf`;

      // PDF 다운로드
      pdf.save(fileName);

    } catch (error) {
      console.error('PDF 생성 실패:', error);
      alert('PDF 생성에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setIsPdfGenerating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="daily-report-modal-overlay" onClick={onClose}>
      <div className="daily-report-modal-content" onClick={(e) => e.stopPropagation()} ref={reportRef}>
        {/* 로딩 중 */}
        {loading && (
          <div className="report-loading">
            <div className="loading-spinner"></div>
            <p>월간보고서를 불러오는 중...</p>
          </div>
        )}

        {/* 에러 */}
        {error && !loading && (
          <div className="report-error">
            <p>{error}</p>
            <div>
              <button onClick={loadReportData}>다시 시도</button>
              <button onClick={onClose}>닫기</button>
            </div>
          </div>
        )}

        {/* 보고서 내용 */}
        {!loading && !error && reportData && (
          <>
            {/* 모달 헤더 */}
            <div className="report-header">
              <button className="close-btn" onClick={onClose}>✕</button>

              <div className="report-title-section">
                <h1 className="report-main-title">월 간 보 고 서</h1>
                <div className="report-subtitle">Monthly Production Report</div>
              </div>

              <div className="report-info-table">
                <table>
                  <tbody>
                    <tr>
                      <th>보고일자</th>
                      <td>{reportData.header.year}년 {reportData.header.month}월</td>
                      <th>구분</th>
                      <td>{reportData.header.category}</td>
                    </tr>
                    <tr>
                      <th>작성자</th>
                      <td>{reportData.header.author}</td>
                      <th>작성일시</th>
                      <td>{new Date(reportData.header.createdAt).toLocaleString('ko-KR')}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* 모달 바디 */}
            <div className="report-body">
              {/* 1. 월간 설비 현황 */}
              <div className="report-section">
                <h2 className="section-number">1. 설비 현황 (월간 집계)</h2>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>항목</th>
                      <th>수량/비율</th>
                      <th>단위</th>
                      <th>비고</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>전체 설비수</td>
                      <td className="value-cell">{reportData.equipmentStatus.totalEquipment}</td>
                      <td>대</td>
                      <td>등록된 총 설비 수</td>
                    </tr>
                    <tr className="summary-row">
                      <td><strong>월간 평균 가동률</strong></td>
                      <td className="value-cell"><strong>{reportData.equipmentStatus.averageOperationRate.toFixed(1)}</strong></td>
                      <td><strong>%</strong></td>
                      <td>월간 종합 가동률</td>
                    </tr>
                    <tr>
                      <td>월간 총 샷 수</td>
                      <td className="value-cell">{reportData.equipmentStatus.totalShots.toLocaleString()}</td>
                      <td>회</td>
                      <td>전체 사출 횟수</td>
                    </tr>
                    <tr className="highlight-row danger">
                      <td>알람 발생 건수</td>
                      <td className="value-cell">{reportData.equipmentStatus.totalAlarmCount.toLocaleString()}</td>
                      <td>건</td>
                      <td>월간 총 알람</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* 2. 월간 생산 실적 */}
              <div className="report-section">
                <h2 className="section-number">2. 생산 실적 (월간 집계)</h2>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>항목</th>
                      <th>수량</th>
                      <th>단위</th>
                      <th>비고</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="highlight-row success">
                      <td>양품 생산량</td>
                      <td className="value-cell">{reportData.equipmentStatus.monthlyProduction.toLocaleString()}</td>
                      <td>개</td>
                      <td>월간 양품</td>
                    </tr>
                    <tr className="highlight-row warning">
                      <td>불량 수</td>
                      <td className="value-cell">{reportData.equipmentStatus.defectCount.toLocaleString()}</td>
                      <td>개</td>
                      <td>월간 불량</td>
                    </tr>
                    <tr className="summary-row">
                      <td><strong>총 생산량</strong></td>
                      <td className="value-cell"><strong>{(reportData.equipmentStatus.monthlyProduction + reportData.equipmentStatus.defectCount).toLocaleString()}</strong></td>
                      <td><strong>개</strong></td>
                      <td>양품 + 불량</td>
                    </tr>
                    <tr>
                      <td>에너지 사용량</td>
                      <td className="value-cell">{reportData.equipmentStatus.energyProduction.toLocaleString('ko-KR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td>kWh</td>
                      <td>전력 사용량</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* 3. 월간 OEE 지표 */}
              <div className="report-section">
                <h2 className="section-number">3. OEE 지표 분석 (월간 평균)</h2>
                <table className="data-table oee-table">
                  <thead>
                    <tr>
                      <th>지표</th>
                      <th>수치</th>
                      <th>진행률</th>
                      <th>평가</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>가용성 (Availability)</td>
                      <td className="value-cell">{reportData.oee.availability.toFixed(1)}%</td>
                      <td className="progress-cell">
                        <div className="progress-bar">
                          <div className="progress-fill availability" style={{ width: `${reportData.oee.availability}%` }}></div>
                        </div>
                      </td>
                      <td>{reportData.oee.availability >= 85 ? '양호' : reportData.oee.availability >= 70 ? '보통' : '개선필요'}</td>
                    </tr>
                    <tr>
                      <td>성능 (Performance)</td>
                      <td className="value-cell">{reportData.oee.performance.toFixed(1)}%</td>
                      <td className="progress-cell">
                        <div className="progress-bar">
                          <div className="progress-fill performance" style={{ width: `${reportData.oee.performance}%` }}></div>
                        </div>
                      </td>
                      <td>{reportData.oee.performance >= 85 ? '양호' : reportData.oee.performance >= 70 ? '보통' : '개선필요'}</td>
                    </tr>
                    <tr>
                      <td>품질 (Quality)</td>
                      <td className="value-cell">{reportData.oee.quality.toFixed(1)}%</td>
                      <td className="progress-cell">
                        <div className="progress-bar">
                          <div className="progress-fill quality" style={{ width: `${reportData.oee.quality}%` }}></div>
                        </div>
                      </td>
                      <td>{reportData.oee.quality >= 95 ? '양호' : reportData.oee.quality >= 90 ? '보통' : '개선필요'}</td>
                    </tr>
                    <tr className="summary-row oee-total">
                      <td><strong>종합 OEE</strong></td>
                      <td className="value-cell"><strong>{reportData.oee.oee.toFixed(1)}%</strong></td>
                      <td className="progress-cell">
                        <div className="progress-bar">
                          <div className="progress-fill oee" style={{ width: `${reportData.oee.oee}%` }}></div>
                        </div>
                      </td>
                      <td><strong>{reportData.oee.oee >= 75 ? '우수' : reportData.oee.oee >= 60 ? '양호' : '개선필요'}</strong></td>
                    </tr>
                  </tbody>
                </table>
                <div className="oee-formula">
                  <p>* OEE = 가용성 × 성능 × 품질</p>
                </div>
              </div>

              {/* 4. AI 종합 분석 */}
              <div className="report-section">
                <h2 className="section-number">4. 종합 분석 및 제언 (월간)</h2>
                <div className="ai-summary-box markdown-content">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {reportData.aiSummary}
                  </ReactMarkdown>
                </div>
              </div>

              {/* 보고서 하단 */}
              <div className="report-footer">
                <div className="footer-actions">
                  <button className="pdf-btn" onClick={handleDownloadPDF} disabled={isPdfGenerating}>
                    {isPdfGenerating ? (
                      <>
                        <span className="pdf-loading-spinner"></span> PDF 생성 중...
                      </>
                    ) : (
                      <>
                        <span>📄</span> PDF로 저장
                      </>
                    )}
                  </button>
                </div>
                <div className="footer-info">
                  <p>본 보고서는 자동으로 생성되었습니다.</p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default MonthlyReportModal;
